import React from "react";
import { Link } from "react-router-dom";
import { Zap, Infinity as InfinityIcon } from "lucide-react";
import { useUsage } from "@/context/UsageContext";
import { useLang } from "@/context/LangContext";
import { t } from "@/i18n";

export default function UsageMeter({ compact = false }) {
  const { usage } = useUsage();
  const { lang } = useLang();
  if (!usage) return null;

  const { used, cap, unlimited, plan } = usage;
  const PLAN_LABEL = {
    free: {es:"Gratis", en:"Free", it:"Gratuito", pt:"Grátis", de:"Kostenlos", zh:"免费", ru:"Бесплатный", ar:"مجاني", hi:"मुफ्त"},
    basic: {es:"Básico", en:"Basic", it:"Base", pt:"Básico", de:"Basic", zh:"基础", ru:"Базовый", ar:"أساسي", hi:"बेसिक"},
    pro: {es:"Pro", en:"Pro", it:"Pro", pt:"Pro", de:"Pro", zh:"专业", ru:"Про", ar:"محترف", hi:"प्रो"},
    ultimate: {es:"Ilimitado", en:"Unlimited", it:"Illimitato", pt:"Ilimitado", de:"Unbegrenzt", zh:"无限", ru:"Безлимит", ar:"غير محدود", hi:"असीमित"},
  };
  const planLabel = PLAN_LABEL[plan]?.[lang] || PLAN_LABEL[plan]?.en || plan;
  if (unlimited) {
    return (
      <Link to="/app/plan" data-testid="usage-meter" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-accent/40 bg-accent/10 text-accent text-xs">
        <InfinityIcon className="w-3.5 h-3.5" strokeWidth={2} />
        <span>{planLabel}</span>
      </Link>
    );
  }

  const remaining = Math.max(0, cap - used);
  const pct = Math.min(100, (used / cap) * 100);
  const critical = remaining <= 1;
  const warning = remaining <= Math.max(1, Math.floor(cap * 0.25));

  const tone = critical
    ? "border-destructive/40 bg-destructive/10 text-destructive"
    : warning
    ? "border-accent/40 bg-accent/10 text-accent"
    : "border-border bg-secondary/40 text-foreground";

  return (
    <Link
      to="/app/plan"
      data-testid="usage-meter"
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs transition-colors hover:brightness-105 ${tone}`}
      title={`${used}/${cap} ${t(lang, "answers")}`}
    >
      <Zap className="w-3.5 h-3.5" strokeWidth={2} />
      <span className="font-medium">{used} / {cap}</span>
      {!compact && (
        <span className="hidden sm:inline text-[10px] uppercase tracking-wider opacity-80">
          {t(lang, "answers")} · {t(lang, "day")}
        </span>
      )}
      <span className="w-10 h-1 rounded-full bg-black/10 overflow-hidden ml-1 hidden sm:block">
        <span className="block h-full bg-current transition-all" style={{ width: `${pct}%` }} />
      </span>
    </Link>
  );
}
